import {
    Edit,
    SimpleForm,
    useTranslate,
    useRecordContext,
    required,
    SelectInput,
    TextInput,
    ArrayInput,
    SimpleFormIterator,
    FormDataConsumer,
    DateTimeInput,
} from 'react-admin';

import { EditToolbar } from '../../common/EditToolbar';
import { SEGMENTATION_MODULE } from '../../common/constants';
import { ConditionMethodsChoice, ConditionTypeChoice, operatorChoices } from './SegmentCreate';
import PermissionGuard from '../../common/PermissionGuard';
import useIsPermission from '../../hooks/useIsPermission';
import { validateNotEmpty } from '../../common/VideoPlayer/util';
import { TestSeriesAutocomplete } from '../../common/inputs/autoCompleteInputs/TestSeriesAutoCompleteInput';
import { BatchAutocomplete } from '../../common/inputs/autoCompleteInputs/BatchAutoComplete';
import { ProgramAutocomplete } from '../../common/inputs/autoCompleteInputs/ProgramAutoComplete';
import { SubjectsAutoComplete } from '../../common/inputs/autoCompleteInputs/SubjectsAutoComplete';

const SegmentEdit = () => {
    const { isUpdatable } = useIsPermission('SEGMENTS');
    return (
        <PermissionGuard permission={isUpdatable}>
            <Edit mutationMode={'pessimistic'} title={<SegmentTitle />}>
                <SimpleForm toolbar={<EditToolbar />}>
                    <TextInput source="name" fullWidth validate={[required(), validateNotEmpty]} />

                    <SelectInput
                        source="module"
                        label="Module"
                        fullWidth
                        defaultValue="BATCH"
                        validate={[required()]}
                        choices={SEGMENTATION_MODULE}
                    />

                    <ArrayInput source="condition">
                        <SimpleFormIterator>
                            <SelectInput
                                fullWidth
                                source="type"
                                label="Type"
                                choices={ConditionTypeChoice}
                                validate={[required()]}
                            />
                            <SelectInput
                                fullWidth
                                source="method"
                                label="Method"
                                choices={ConditionMethodsChoice}
                                validate={[required()]}
                            />
                            <FormDataConsumer>
                                {({ scopedFormData, getSource }) => {
                                    const selectedType = ConditionTypeChoice.find(
                                        opt => opt.id === scopedFormData?.type,
                                    )?.name;

                                    switch (selectedType) {
                                        case 'Batch':
                                            return (
                                                <BatchAutocomplete
                                                    source={getSource?.('value') ?? 'value'}
                                                    validate={[required()]}
                                                />
                                            );
                                        case 'Programs':
                                            return (
                                                <ProgramAutocomplete
                                                    source={getSource?.('value') ?? 'value'}
                                                    validate={[required()]}
                                                />
                                            );
                                        case 'Subjects':
                                            return (
                                                <SubjectsAutoComplete
                                                    source={getSource?.('value') ?? 'value'}
                                                    validate={[required()]}
                                                />
                                            );
                                        case 'Test Series':
                                            return (
                                                <TestSeriesAutocomplete
                                                    source={getSource?.('value') ?? 'value'}
                                                    validate={[required()]}
                                                />
                                            );
                                        case 'User':
                                            return (
                                                <DateTimeInput
                                                    source={getSource?.('value') ?? 'value'}
                                                    fullWidth
                                                    validate={[required()]}
                                                />
                                            );
                                        default:
                                            return null;
                                    }
                                }}
                            </FormDataConsumer>

                            <SelectInput
                                source="operator"
                                fullWidth
                                label="Operator"
                                choices={operatorChoices}
                                validate={[required()]}
                            />
                        </SimpleFormIterator>
                    </ArrayInput>
                </SimpleForm>
            </Edit>
        </PermissionGuard>
    );
};

const SegmentTitle = () => {
    const record = useRecordContext<any>();
    const translate = useTranslate();

    return record ? (
        <span>
            {translate('Segment', { smart_count: 1 })} &quot;
            {record.module}&quot;
        </span>
    ) : null;
};

export default SegmentEdit;
